import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import type { API } from '@hero/api';
import { DEFAULT_LIMIT, DEFAULT_RETRY } from '../config';
import type {
  UseHookInfiniteQueryOptions,
  InternalHookMutationOptions,
  InternalHookQueryOptions,
  InternalHookFileDownloadOptions,
  RunQueryOptions,
} from '../types';
import type {
  ExtractAPIFromHookMutate,
  FileDownloadApiReturn,
  HookMutateApiConfig,
} from '../types/hook-api';
import { getListQueryKey, getOneQueryKey, getQueryDefaults, getQueryKeyFromParams, resolveQueryEnabled } from './query';
import { runInvalidateQueryKeys } from './mutation';
import { useInfinitePagination } from './useInfinitePagination';
import { getObjectId } from '@hero/schema';
import { useIsSessionAuthenticated } from '../auth/session-gate';

export const useQueryAPI = <T extends API<any, any>>(config: InternalHookQueryOptions<T>) => {
  const queryClient = useQueryClient();

  const getSingleIdQuery = () => {
    if (config.singleIdQuery) {
      return { _id: getObjectId(config.singleIdQuery) } as T['req'];
    }
    return undefined;
  };
  const {
    entity,
    query = getSingleIdQuery(),
    apiFn: defaultApiFn,
    apiConfig,
    isEnabled,
    queryKeyScope,
  } = config;
  const isAuthenticated = useIsSessionAuthenticated();

  const [requestedParams, setRequestedParams] = useState<T['req'] | undefined>(undefined);
  const effectiveParams = (requestedParams ?? query) as Record<string, unknown> | undefined;
  const apiFn = apiConfig?.apiFn ?? defaultApiFn;
  const scopeKey = useCallback(
    (params: Record<string, unknown> | undefined) => {
      const base = getQueryKeyFromParams(entity, params);
      return queryKeyScope ? [...base, queryKeyScope] : base;
    },
    [entity, queryKeyScope],
  );
  const queryKey = scopeKey(effectiveParams);
  const defaults = getQueryDefaults(entity);

  const queryResult = useQuery({
    queryKey,
    queryFn: () => apiFn(effectiveParams as T['req']),
    enabled: resolveQueryEnabled(apiConfig, isEnabled, isAuthenticated),
    ...defaults,
  });

  const runQuery = useCallback(
    async (params: T['req']) => {
      const data = await queryClient.fetchQuery({
        queryKey: scopeKey(params as Record<string, unknown>),
        queryFn: () => apiFn(params),
        ...defaults,
      });
      setRequestedParams(params);
      return data;
    },
    [queryClient, scopeKey, apiFn, defaults],
  );

  return { ...queryResult, runQuery };
};
export type UseQueryApiReturn<Data> = ReturnType<typeof useQueryAPI<API<any, Data>>>;

/** mutate(variables) => void. Uses default apiFn unless apiConfig.apiFn is provided. */
export function useMutationAPI<H extends HookMutateApiConfig<API<any, any>>>(
  config: InternalHookMutationOptions<H>,
): UseMutationResult<
  ExtractAPIFromHookMutate<H>['data'],
  Error,
  ExtractAPIFromHookMutate<H>['req']
> {
  const { entity, apiFn: defaultApiFn, apiConfig, defaultInvalidationStrategy } = config;
  const queryClient = useQueryClient();
  type T = ExtractAPIFromHookMutate<H>;

  const apiFn = (apiConfig?.apiFn ?? defaultApiFn) as (variables: T['req']) => Promise<T['data']>;
  const invalidationStrategy = apiConfig?.invalidationStrategy ?? defaultInvalidationStrategy;

  return useMutation<T['data'], Error, T['req']>({
    mutationFn: apiFn,
    onSuccess: data => {
      runInvalidateQueryKeys(queryClient, {
        entity,
        invalidationStrategy,
        invalidateQueryKeys: apiConfig?.invalidateQueryKeys,
      });
      apiConfig?.onSuccess?.(data);
    },
    onError: apiConfig?.onError,
  });
}

/**
 * Download a file (blob) from an API endpoint. Handles blob response, triggers
 * browser download, and manages isPending state. No cache invalidation.
 *
 * Domain hooks pass the SDK API method (which has .withRes); consumer can
 * override filename, onSuccess, and onError via apiConfig.
 */
export function useFileDownloadAPI<T extends API<any, any>>(
  config: InternalHookFileDownloadOptions<T>,
): FileDownloadApiReturn<T> {
  const { apiFn, apiConfig, defaultFilename, defaultOnSuccess, defaultOnError } = config;
  const [isPending, setIsPending] = useState(false);

  const download = useCallback(
    async (req: T['req']) => {
      setIsPending(true);
      try {
        const blob = (await apiFn.withRes(req, {
          responseType: 'blob',
        })) as unknown as Blob;

        const resolvedName = apiConfig?.filename ?? defaultFilename;
        const name =
          typeof resolvedName === 'function'
            ? resolvedName(req)
            : (resolvedName ?? `export-${Date.now()}.xlsx`);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        (apiConfig?.onSuccess ?? defaultOnSuccess)?.();
      } catch (error) {
        (apiConfig?.onError ?? defaultOnError)?.(error as Error);
      } finally {
        setIsPending(false);
      }
    },
    [apiFn, apiConfig, defaultFilename, defaultOnSuccess, defaultOnError],
  );

  return { download, isPending };
}

export const useInfiniteQueryAPI = <T extends API<any, any>>(
  config: UseHookInfiniteQueryOptions<T>,
) => {
  const { entity, apiFn: defaultApiFn, query: baseQuery, apiConfig, isEnabled } = config;
  const queryClient = useQueryClient();
  const isAuthenticated = useIsSessionAuthenticated();

  const apiFn = apiConfig?.apiFn ?? defaultApiFn;
  const base = baseQuery as Record<string, unknown> & { limit?: number };
  const limit = apiConfig?.limit ?? base?.limit ?? DEFAULT_LIMIT;
  const defaults = getQueryDefaults(entity);

  const [overlay, setOverlay] = useState<Record<string, unknown>>({});

  // Stabilize baseQuery so we only reset overlay when its value changes, not reference.
  // Callers often pass inline objects (e.g. { unifiedStatus: 'x' }) which are new refs every render.
  const baseQueryKey = useMemo(() => JSON.stringify(baseQuery ?? {}), [baseQuery]);

  useEffect(() => {
    setOverlay({});
  }, [baseQueryKey]);

  const effectiveQuery = useMemo(
    () => ({ ...base, ...overlay, limit } as Record<string, unknown> & { limit?: number }),
    [base, overlay, limit],
  );

  // this is a custom query to run ... useInfiniteQueryAPI automaticlly automaticlly run when enabled is true.
  // but in some cases we want to run the query manually with diffrent fitlers or anything withouth have to change the base query.
  // this case happens a lot in things like SelectAsync component ... where we want to run the query manually with diffrent fitlers or anything withouth have to change the base query.
  const runQuery = useCallback((partialQuery: Partial<T['req']>, options?: RunQueryOptions) => {
    const merge = options?.merge !== false;
    setOverlay(prev =>
      merge ? { ...prev, ...partialQuery } : (partialQuery as Record<string, unknown>),
    );
  }, []);

  const result = useInfinitePagination<T['data'] extends (infer U)[] ? U : never>({
    queryKey: getListQueryKey(entity, effectiveQuery ?? {}),

    queryFn: ({ skip, limit: pageLimit }) =>
      apiFn({ ...effectiveQuery, skip, limit: pageLimit } as T['req']),
    limit,
    enabled: resolveQueryEnabled(apiConfig, isEnabled, isAuthenticated),
    staleTime: defaults.staleTime,
    retry:
      typeof defaults.retry === 'boolean' ? (defaults.retry ? DEFAULT_RETRY : 0) : defaults.retry,
  });

  // Cache seeding: populate single-item cache for each item in the list.
  // This eliminates redundant get-one requests when opening drawers.
  useEffect(() => {
    if (!result.data?.length) return;
    for (const item of result.data) {
      if (item && typeof item === 'object' && '_id' in item) {
        const id = (item as { _id: unknown })._id;
        const key = getOneQueryKey(entity, id as string);
        queryClient.setQueryData(key, item);
      }
    }
  }, [result.data, entity, queryClient]);

  return { ...result, runQuery };
};

export type UseInfiniteQueryApiReturn<Data> = ReturnType<
  typeof useInfiniteQueryAPI<API<any, Data>>
>;
